import { Component, ElementRef, EventEmitter, Inject, Output, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogConfig, MatDialogRef } from '@angular/material/dialog';
import { 
  EmployerHoursBanksForm, 
  EntitlementRuleFormGroup, 
  HoursBankBucketPost, 
  StateNameFormGroup} from '@core/models/leave-admin/employers/employer-hours-banks.model';
import { EmployerLeaveHoursBank, EmployerLeaveHoursBucket, EmployerProfile, LeaveHoursBankEntitlementRule, LeaveHoursBankStateName, LeaveHoursBucketStateName } from '@core/models/leave-admin/employers/employer-profile.model';
import { ErrorService, LayoutService } from '@core/services';
import { EmployerProfileStoreService } from '@core/services/employer-profile-store.service';
import { ManageEmployersService } from '@core/services/manage-employers.service';
import { Subject, Observable } from 'rxjs';
import { position, onDragEnded } from '@shared/helpers/dialog-drag-constraints';
import { indicate, noChangesReplacer, closeDialogOnEscape } from '@shared/helpers';
import { UnsavedChangesComponent } from '../unsaved-changes/unsaved-changes.component';
import { takeUntil } from 'rxjs/operators';
import { SelectionOption, StiiraError } from '@core/models';
import { Banks, Buckets } from '@core/enums';
import { UpdateConfirmationDialog } from '@core/models/shared/update-confirmation-dialog.model';
import { UpdateConfirmationComponent } from '../update-confirmation/update-confirmation.component';

export interface EditEmployerHoursBanksComponentData {
  employerId: number;
  isBank: boolean; // edit bank vs bucket
  bank: EmployerLeaveHoursBank | EmployerLeaveHoursBucket;
  stateSelectionList: SelectionOption[];
  quantityMeasureList: SelectionOption[];
  leaveReasonSelectionList: SelectionOption[];
  sysText: any;
}

@Component({
    selector: 'app-edit-employer-hours-banks',
    templateUrl: './edit-employer-hours-banks.component.html',
    styleUrl: './edit-employer-hours-banks.component.scss',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class EditEmployerHoursBanksComponent {
  static readonly componentName = 'EditEmployerHoursBanksComponent';
  
  @Output() isEditing = new EventEmitter<boolean>();

  @ViewChild('addStateButton', { read: ElementRef }) private addStateButton: ElementRef<HTMLButtonElement>;
  @ViewChild('addRuleButton', { read: ElementRef }) private addRuleButton: ElementRef<HTMLButtonElement>;

  public form: FormGroup<EmployerHoursBanksForm>;
  public isSubmitting$: Subject<boolean> = new Subject<boolean>();
  public position = position;
  public onDragEnded = onDragEnded;
  // keep track of each additional state picklist which will 
  // not include any other states already selected 
  public stateSelectionOptions: SelectionOption[][] = [];

  private formInitValues: any;
  private destroy$: Subject<void> = new Subject<void>();
  private formChangeEmitted: boolean = false;

  get noChanges(): boolean {
    return JSON.stringify(this.form.value, noChangesReplacer) === JSON.stringify(this.formInitValues, noChangesReplacer);
  }

  public isHandheld$: Observable<boolean>;
  public isMobile$: Observable<boolean>;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: EditEmployerHoursBanksComponentData,
    private fb: FormBuilder,
    private layoutService: LayoutService,
    private dialogRef: MatDialogRef<EditEmployerHoursBanksComponent>,
    private store: EmployerProfileStoreService,
    private dialog: MatDialog,
    private service: ManageEmployersService,
    private errorService: ErrorService,
  ) { 
    this.form = this.fb.group<EmployerHoursBanksForm>({
      bankId: new FormControl<number>(null),
      defaultName: new FormControl<string>(null),
      primaryName: new FormControl<string>(null),
      primaryFullDayIncrements: new FormControl<boolean>(false),
      stateSpecificBankNames: new FormArray<FormGroup<StateNameFormGroup>>([]),
      entitlementRules: new FormArray<FormGroup<EntitlementRuleFormGroup>>([]),
      isProtected: new FormControl<boolean>(null),
    });
  }

   ngOnInit(): void {
    this.isHandheld$ = this.layoutService.isHandheld$();
    this.isMobile$ = this.layoutService.isMobile$();

    closeDialogOnEscape(this.dialogRef, this.isSubmitting$, () => this.close(false));

    this.setupForm();
    this.data.stateSelectionList;
  }

  public onSubmit(overrideUpdateConf: boolean = false) {
    this.form.markAsUntouched();
    const dto: HoursBankBucketPost = {
      overrideUpdateConf: overrideUpdateConf,
      employerId: this.data.employerId,
      bank: this.data.isBank ? this.form.controls.bankId.value as Banks : null,
      bucket: this.data.isBank ? null : this.form.controls.bankId.value as Buckets,
      primaryName: this.form.controls.primaryName.value,
      primaryFullDayIncrements: this.data.isBank ? this.form.controls.primaryFullDayIncrements.value : null,
      stateSpecificBankNames: this.form.controls.stateSpecificBankNames.controls.map(fg => {
        return {
          stateId: fg.controls.stateId.value,
          stateSpecificName: fg.controls.stateSpecificName.value,
          stateFullDayIncrements: this.data.isBank ? fg.controls.stateFullDayIncrements.value : null
        };
      }),
      entitlementRules: this.data.isBank ? this.form.controls.entitlementRules.controls.map(fg => {
        return {
          id: fg.controls.id.value,
          stateId: fg.controls.stateId.value,
          leaveReasonId: fg.controls.leaveReasonId.value,
          quantity: fg.controls.quantity.value,
          quantityMeasure: fg.controls.quantityMeasure.value,
          oneTimeEntitlement: fg.controls.oneTimeEntitlement.value
        };
      }) : null,
      isProtected:  this.data.isBank ? this.form.controls.isProtected.value : null
    }

    this.service.postLeaveHoursBanksEdit(dto)
      .pipe(indicate(this.isSubmitting$))
      .subscribe((res)=>{
        if (res.showUpdateConfirmationDialog) {
          this.openUpdateConfirmationDialog(res as UpdateConfirmationDialog);
        } else {
          this.store.employerProfile = res as EmployerProfile;
          this.dialogRef.close();
        }
    },(err: StiiraError) => this.errorService.setFormModelStateErrors(this.form, err.modelStateErrors))
  }

  public close(canNavigate: boolean): void {
    if (canNavigate) {
      this.dialogRef.close();
    } else {
      if (this.noChanges) {
        this.dialogRef.close();
      } else {
        this.openUnsavedChangesDialog();
      }
    }
  }

  public onBankAddStateName(): void {
    const sfg = this.CreateStateNameFormGroup();
    this.form.controls.stateSpecificBankNames.push(sfg);
    this.stateSelectionOptions.push(this.getBankAvailableStateOptions(sfg));
  }

  public onBankRemoveStateName(stateIndex: number): void {
    // the pressed button goes with its row
    this.addStateButton.nativeElement.focus();
    this.form.controls.stateSpecificBankNames.removeAt(stateIndex);
    this.stateSelectionOptions.splice(stateIndex, 1);
    this.setAllBanksAvailableStateOptions();
  }

  public onBankAddRule(): void {
    this.form.controls.entitlementRules.push(this.CreateEntitlementRuleFormGroup());
  }

  public onBankRemoveRule(ruleIndex: number): void {
    // the pressed button goes with its row
    this.addRuleButton.nativeElement.focus();
    this.form.controls.entitlementRules.removeAt(ruleIndex);
  }

  private openUnsavedChangesDialog(): void {
    const dialogConfig: MatDialogConfig = {
      id: UnsavedChangesComponent.componentName,
      width: '300px',
      data: this.store.sysText.unsavedChanges,
    };
    
    this.dialog.open(UnsavedChangesComponent, dialogConfig)
      .afterClosed().subscribe((res: boolean) => {
        if (res) {
          this.dialogRef.close();
        }
      });
  }

  private CreateStateNameFormGroup(
    stateId: number = null, 
    customName: string = null,
    stateFullDayIncrements: boolean = false): 
    FormGroup<StateNameFormGroup> {
    const sfg =  this.fb.group<StateNameFormGroup>({
      stateId: this.fb.control(stateId),
      stateSpecificName: this.fb.control(customName),
      stateFullDayIncrements: this.fb.control(stateFullDayIncrements)
    });
    
    sfg.controls.stateId.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.setAllBanksAvailableStateOptions();
      });
    return sfg;
  }

  private CreateEntitlementRuleFormGroup(rule: LeaveHoursBankEntitlementRule = null): FormGroup<EntitlementRuleFormGroup> {
    const erfg = this.fb.group<EntitlementRuleFormGroup>({
      id: this.fb.control(rule?.id ?? null),
      stateId: this.fb.control(rule?.state?.id as number ?? null),
      leaveReasonId: this.fb.control(rule?.leaveReason?.id as number ?? null),
      quantity: this.fb.control(rule?.quantity ?? null),
      quantityMeasure: this.fb.control(rule?.quantityMeasure),
      oneTimeEntitlement: this.fb.control(rule?.oneTimeEntitlement ?? false)
    });
    return erfg
  }

  private setupForm(): void {
    this.form.patchValue({
      bankId: this.data.bank.id,
      defaultName: this.data.bank.defaultName,
      primaryName: this.data.bank.primaryName,
      primaryFullDayIncrements: this.data.isBank ? (this.data.bank as EmployerLeaveHoursBank).primaryFullDayIncrements : null
    });

    this.data.bank.stateSpecificNames.forEach((sn: LeaveHoursBankStateName | LeaveHoursBucketStateName) => {
      this.form.controls.stateSpecificBankNames
        .push(this.CreateStateNameFormGroup(sn.stateId, sn.stateSpecificName, (sn as LeaveHoursBankStateName).stateFullDayIncrements));
    });

    if (this.data.isBank) {
      (this.data.bank as EmployerLeaveHoursBank).entitlementRules.forEach(er => {
        this.form.controls.entitlementRules
          .push(this.CreateEntitlementRuleFormGroup(er))
        });
      this.form.controls.isProtected.patchValue((this.data.bank as EmployerLeaveHoursBank).isProtected);
    }

    // set up independant selection lists for each existing state picklist 
    // (can only select each state once per bank)

    this.form.controls.stateSpecificBankNames.controls.forEach((sfg) => {
      this.stateSelectionOptions.push(this.getBankAvailableStateOptions(sfg))
    });

    this.form.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        if (!this.formChangeEmitted && !this.noChanges) {
          this.isEditing.emit(true);
          this.formChangeEmitted = true;
        } else if (this.noChanges) {
          this.isEditing.emit(false);
          this.formChangeEmitted = false;
        }
      });

    this.formInitValues = JSON.parse(JSON.stringify(this.form.value));
  }

  private getBankAvailableStateOptions(
    stateFormGroup: FormGroup<StateNameFormGroup>): SelectionOption[] {
    const selectedStateId = stateFormGroup.controls.stateId.value;
    const bankSelectedStateIds = this.form.controls.stateSpecificBankNames.controls
      .map(sbn => sbn.controls.stateId.value);
    // remove currently selected state for this state group
    const i = bankSelectedStateIds.indexOf(selectedStateId);
    if (i !== -1) {
      bankSelectedStateIds.splice(i,1);
    }
    // available state selections is the stateSelectionList minus any 
    // states already selected for this bank
    const availableStateSelections = this.data.stateSelectionList
      .filter(ss => !bankSelectedStateIds.some(ssid => ss.id == ssid));
    return availableStateSelections;
  }

  private setAllBanksAvailableStateOptions(): void {
    this.form.controls.stateSpecificBankNames.controls.forEach((sfg, si) => {
      this.stateSelectionOptions[si] = this.getBankAvailableStateOptions(sfg);
    });
  }

  private openUpdateConfirmationDialog(updateConfirmationDialog: UpdateConfirmationDialog): void {
    const dialogConfig: MatDialogConfig = {
      id: UpdateConfirmationComponent.componentName,
      width: "450px",
      panelClass: "mat-dialog-container-mobileWidth",
      disableClose: false,
      data: {
        sysText: this.store.sysText.updateConfirmation,
        updateConfirmationDialog: updateConfirmationDialog
      }
    };

    this.dialog.open(UpdateConfirmationComponent, dialogConfig)
      .beforeClosed().subscribe((res: boolean) => {
        if (res) {
          this.onSubmit(true);
        }
      });
  }
}
